import {
  IPicture,
  ISimplePicture,
  PICTURE_FORMAT,
} from 'affiny-core/modules/pictures/types';
import { IRootState } from 'affiny-core/modules/state';
import get from 'lodash/get';

/* ***** *****  Pictures  ***** ***** */

export const selectPictureByUri = (state: IRootState, id: string) =>
  get(state, `pictures.entries.${id}`);

export const selectPictureUriById = (
  state: IRootState,
  id: string,
  format: PICTURE_FORMAT = PICTURE_FORMAT.BIG,
  isBlurredFormat?: boolean
) =>
  get(
    selectPictureByUri(state, id),
    `attributes[${isBlurredFormat ? 'blurredFormats' : 'formats'}].${format}`
  );

export const selectPictureUriByIdArray = (
  state: IRootState,
  ids: string[],
  format: PICTURE_FORMAT,
  isBlurredFormat?: boolean
) => ids.map(id => selectPictureUriById(state, id, format, isBlurredFormat));

export const selectMainPictureUriByAccountId = (
  state: IRootState,
  accountId: string,
  format: PICTURE_FORMAT
) => {
  const mainPictureId = selectMainPictureIdByAccountId(state, accountId);

  if (!mainPictureId) return;

  return selectPictureUriById(state, mainPictureId, format);
};

export const selectPicturesByAccountId = (
  state: IRootState,
  accountId: string
) =>
  Object.keys(state.pictures.entries)
    .filter(
      (key: string) =>
        state.pictures.entries[key].attributes.aboid === accountId
    )
    .map((key: string) => state.pictures.entries[key])
    .sort(mainPictureFirst);

export const selectSimplePicturesByAccountId = (
  state: IRootState,
  accountId: string
): ISimplePicture[] => {
  const pictureList = selectPicturesByAccountId(state, accountId);

  return pictureList.map(picture => ({
    source: picture.attributes.formats.BIG,
    id: picture.id,
  }));
};

/* ***** *****  Main picture  ***** ***** */

const isMainPicture = (picture: IPicture) => picture.attributes.isMainPicture;

export const mainPictureFirst = (a: IPicture, b: IPicture) => {
  if (isMainPicture(a)) {
    return -1;
  }

  if (isMainPicture(b)) {
    return 1;
  }

  return 0;
};

export const getMainPicture = (pictures: IPicture[]) =>
  pictures.find(isMainPicture);

export const selectMainPictureByAccountId = (
  state: IRootState,
  accountId: string
) =>
  Object.values(state.pictures.entries)
    .filter(picture => picture.attributes.aboid === accountId)
    .find(isMainPicture);

export const selectMainPictureIdByAccountId = (
  state: IRootState,
  accountId: string
) => {
  const mainPicture = selectMainPictureByAccountId(state, accountId);

  return mainPicture && mainPicture.id;
};
